import React from 'react'

import logger from '@/utils/logger'

import TextWithLines from '@/components/TextWithLines/TextWithLines'
import { useToaster } from '@/components/Toast/useToaster'

/**
 * Wraps around a callback function that typically contains an API call
 * to create a more pleasant user experience by:
 * 1) using a lock so that if `callback` is executing, additional calls to it will be ignored
 * 2) error handling that shows a toast to the user
 *
 * Note: If you change the name of this function, make sure to change the regex
 * in the 'react-hooks/exhaustive-deps' rule in .eslintrc.js. To continue
 * using the lint rule, ensure that the argument order contains the callback
 * first and deps second.
 *
 * Example Usage:
 * const [onSubmit, isSubmitting] = useAPIWrapper(args => {
 *   ...
 * }, [dep1, dep2], 'Perform API call')
 */
export default function useAPIWrapper<TArgs extends unknown[]>(
  callback: (...args: TArgs) => Promise<void>,
  deps: React.DependencyList,
  label: string
): [(...args: TArgs) => Promise<void>, boolean] {
  const toast = useToaster()

  const [isLoading, setIsLoading] = React.useState(false)
  const isLoadingRef = React.useRef(false)

  const wrapper = React.useCallback(async (...args: TArgs) => {
    if (isLoadingRef.current) {
      // Disallow concurrent execution
      return
    }
    setIsLoading(true)
    isLoadingRef.current = true
    try {
      await callback(...args)
    } catch (err) {
      logger.warning(`Error performing API: ${label}`, { err })
      toast({
        title: (
          <TextWithLines text={`${err?.message ?? err}`} maxLines={Number.POSITIVE_INFINITY} />
        ),
        status: 'error',
      })
    } finally {
      setIsLoading(false)
      isLoadingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return [wrapper, isLoading]
}
